import type { ReactNode } from 'react';
import { T } from 'gt-react';
import type { CompanyListItem } from '@erp/rpc';
import { Combobox } from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import { CompanyOption } from './CompanyOption';
import {
  companyPickerTriggerLabel,
  companyPickerValue,
  explodePricelistPickerCompanies,
  rankPricelistPickerOptions,
} from './companyPickerDisplay';
import type { UseCompanyPickerResult } from './useCompanyPicker';

interface CompanyPickerProps {
  picker: UseCompanyPickerResult;
  /** Shown on the trigger when no company is selected. */
  triggerPlaceholder: string;
  searchPlaceholder: string;
  emptyState: ReactNode;
  disabled?: boolean;
  /** When set, the first option clears the selection (upload flow). */
  unassignedLabel?: ReactNode;
  /**
   * Extra classes on the trigger. The trigger is full-width by default (it
   * sits in forms); a toolbar passes a fixed width and height here so it lines
   * up with the controls beside it instead of taking a row of its own.
   */
  triggerClassName?: string;
}

/** One picker row: a company, or the Unassigned row (`company` null). */
interface CompanyPickerRow {
  value: string;
  company: CompanyListItem | null;
}

// Company ids are UUIDs, so this can't collide with a company option.
const UNASSIGNED = '__unassigned__';

function rowValue(row: CompanyPickerRow): string {
  return row.value;
}

export function CompanyPicker({
  picker,
  triggerPlaceholder,
  searchPlaceholder,
  emptyState,
  disabled = false,
  unassignedLabel,
  triggerClassName,
}: CompanyPickerProps) {
  const vendorFirst = picker.forPricelistManagement;
  const triggerLabel = companyPickerTriggerLabel(
    picker.selectedCompany,
    triggerPlaceholder,
    vendorFirst,
  );
  const companies = vendorFirst
    ? rankPricelistPickerOptions(
        explodePricelistPickerCompanies(picker.companies),
        picker.debouncedSearch,
      )
    : picker.companies;
  const unassignedRows: CompanyPickerRow[] =
    unassignedLabel === undefined ? [] : [{ value: UNASSIGNED, company: null }];
  const companyRows = companies.map((company) => ({
    value: companyPickerValue(company, vendorFirst),
    company,
  }));
  // While searching, Unassigned trails the matches: first, it would take the
  // auto-highlight and Enter would clear the selection.
  const rows = picker.search
    ? [...companyRows, ...unassignedRows]
    : [...unassignedRows, ...companyRows];
  // With an Unassigned row, "nothing picked" is that row, so it shows as
  // selected (as the bespoke list marked it).
  const selectedValue =
    picker.selectedCompany === null
      ? unassignedLabel === undefined
        ? null
        : UNASSIGNED
      : companyPickerValue(picker.selectedCompany, vendorFirst);

  return (
    <Combobox<CompanyPickerRow>
      items={rows}
      getValue={rowValue}
      getLabel={(row) => row.company?.name ?? ''}
      value={selectedValue}
      // Re-picking the company resolves to the trigger placeholder below
      // (company: null), which would clear it; keep it and just close.
      onValueChange={(value, row) => {
        if (value === selectedValue && picker.selectedCompany !== null) {
          picker.setOpen(false);
          return;
        }
        picker.selectCompany(row?.company ?? null);
      }}
      // The selection may predate the loaded page (an existing assignment),
      // so the trigger names it from the hook, not from the rows.
      selectedItems={
        selectedValue === null ? [] : [{ value: selectedValue, company: null }]
      }
      renderValue={() =>
        picker.selectedCompany === null ? (
          <span className="text-muted-foreground">{triggerLabel}</span>
        ) : (
          triggerLabel
        )
      }
      renderItem={(row) =>
        row.company === null ? (
          unassignedLabel
        ) : (
          <CompanyOption company={row.company} vendorFirst={vendorFirst} />
        )
      }
      search={picker.search}
      onSearchChange={picker.setSearch}
      filter={null}
      open={picker.open}
      onOpenChange={picker.setOpen}
      loading={picker.isLoading}
      disabled={disabled}
      className={cn('h-9 font-normal', triggerClassName)}
      contentClassName="w-(--anchor-width)"
      labels={{
        placeholder: triggerPlaceholder,
        search: searchPlaceholder,
        empty: emptyState,
        loading: (
          <T context="loading state, company picker while listCompanies is fetching">
            Loading…
          </T>
        ),
      }}
    />
  );
}
